import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, query, collection, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateProductSlug } from '../utils/seoUtils';
import { useCart } from '../context/CartContext';
import CartPopup from '../components/CartPopup';
import { useEnhancedAddToCart } from '../hooks/useEnhancedAddToCart';
import { soundEffects } from '../utils/soundEffects';
import AppImage from '../components/ui/AppImage';
import './ProductPage.css';
import { Cpu, Layers, HardDrive, Monitor, ShieldCheck, Award, MessageCircle, PhoneCall } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import nexawaveLaptop from '../assets/nexawave_laptop.png';

gsap.registerPlugin(ScrollTrigger);

const laptopPieces = [

  {
    id: 1,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_163ee35b7-1772158080738.png",
    alt: 'Dell Latitude 7410 business laptop on a minimalist wooden desk',
    label: 'Dell Latitude',
    decade: '10th Gen',
    detail: 'Intel Core i5 — Performance Series',
    condition: 'Mint',
    price: '$385',
    size: '14" FHD',
    tag: 'Business'
  },
  {
    id: 2,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_172affdc3-1772158080130.png",
    alt: 'HP EliteBook aluminum chassis with crisp keyboard detail',
    label: 'HP EliteBook',
    decade: '11th Gen',
    detail: 'Backlit Keyboard, Touch ID',
    condition: 'Certified',
    price: '$420',
    size: '13.3" IPS',
    tag: 'Premium'
  },
  {
    id: 3,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_136bc9985-1772158079290.png",
    alt: 'ThinkPad Carbon X1 sleek black profile',
    label: 'Lenovo X1',
    decade: '9th Gen',
    detail: 'Ultra-lightweight Carbon Fiber',
    condition: 'Excellent',
    price: '$460',
    size: '14" 4K',
    tag: 'Pro'
  }];

// Local product images — ideal dimensions: 800 × 600 px (4:3 landscape)
const sliderImages = [
  "/images/product/Best value for money offer.webp",
  "/images/product/Fully tested and verified devices.webp",
  "/images/product/Ready to use out of the box.webp",
  "/images/product/Scratchless premium refurbished laptops.webp",
  "/images/product/Warranty support for desktop computers.webp"
];

// ─── Smooth CSS-Marquee Image Carousel (like the ticker) ─────────────────────
function ProductImageCarousel() {
  const total = sliderImages.length;
  // Duplicate so -50% animation loops perfectly (5 real + 5 clone = 10 cards)
  const loopImages = [...sliderImages, ...sliderImages];

  const imageLabels = [
    'Best Value for Money',
    'Fully Tested & Verified',
    'Ready to Use',
    'Scratchless Premium',
    'Warranty Support',
  ];
  const imageSubs = [
    'Unbeatable refurbished deals',
    'Rigorous quality checks',
    'Plug-in and go',
    'Grade A cosmetic condition',
    'Direct after-sale support',
  ];

  return (
    <section className="product-carousel-section">
      <div className="product-carousel-viewport">
        <div className="product-carousel-track">
          {loopImages.map((src, idx) => {
            const ri = idx % total;
            return (
              <div key={idx} className="product-carousel-card">
                <div className="product-carousel-img-wrap">
                  <img
                    src={src}
                    alt={imageLabels[ri]}
                    className="product-carousel-img"
                    draggable={false}
                    loading={idx < total + 3 ? 'eager' : 'lazy'}
                  />
                  <div className="product-carousel-img-overlay" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CateringKitSection({ staggerRef, product }) {
  return (
    <section
      className="py-32 bg-white relative overflow-hidden border-t border-gray-50 catering-kit-section"
      style={{ paddingBottom: '2.5rem' }}
    >
      {/* Background watermark */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        style={{
          maskImage: 'linear-gradient(180deg, transparent, white 20%, white 80%, transparent)',
          WebkitMaskImage: 'linear-gradient(180deg, transparent, white 20%, white 80%, transparent)'
        }}>

        <span className="font-fraunces text-[14vw] font-bold text-[#0063B8]/[0.03] tracking-tighter whitespace-nowrap">
          NEXAWAVE
        </span>
      </div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-gold/4 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left: Image with floating card */}
          <div className="relative order-2 lg:order-1 catering-image-column">
            <div className="relative overflow-hidden rounded-2xl">
              <AppImage
                src="/images/product/productdetails.webp"
                alt="Lenovo L14 laptop technical details and internal components view"
                width={600}
                height={500}
                className="w-full h-[500px] object-cover img-reveal" />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

              {/* Floating badge */}
              <div className="absolute top-6 left-6 bg-[#0063B8] rounded-xl px-5 py-3 shadow-2xl">
                <div className="text-[10px] text-white/70 tracking-wider uppercase mb-1 font-bold">Inspection Status</div>
                <div className="font-fraunces text-2xl text-white font-medium">Refurbished A+</div>
              </div>
            </div>

            {/* Floating feature card */}
            <div
              className="absolute -bottom-6 -right-6 bg-[#0063B8] rounded-2xl p-6 max-w-xs shadow-2xl animate-float-gentle border border-white/10 catering-floating-card"
              style={{ animationDelay: '1s' }}>

              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shadow-inner">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="text-white">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-sm font-bold text-white uppercase tracking-wider">Certified Grade A+</span>
              </div>
              <p className="text-xs text-white/80 leading-relaxed font-medium">Undergoes a rigorous 50-point technical inspection to ensure peak performance and reliability.</p>
            </div>
          </div>

          {/* Right: Content */}
          <div ref={staggerRef} className="order-1 lg:order-2 stagger-children">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gold opacity-60" />
              <span className="text-xs font-medium tracking-ultra text-[#0063B8] uppercase">
                Technical Data
              </span>
            </div>
            <h2 className="font-fraunces text-5xl md:text-6xl font-medium text-[#1A1A1A] leading-tight tracking-tight mb-4">
              {product?.brand} {product?.model || product?.name}
              <span className="block italic text-[#0063B8]">Specifications.</span>
            </h2>

            {/* Feature list */}
            <div className="space-y-6">
                <div className="flex items-start gap-5 group">
                  <div className="w-11 h-11 rounded-xl bg-[#0063B8] flex items-center justify-center text-white shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2v20M2 12h20" strokeLinecap="round" />
                      <rect x="6" y="6" width="12" height="12" rx="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Processor & RAM</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{product?.processor || 'Intel Core'} • {product?.ramSize || '16GB'} RAM</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 group">
                  <div className="w-11 h-11 rounded-xl bg-[#0063B8] flex items-center justify-center text-white shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="4" y="4" width="16" height="16" rx="2" strokeLinecap="round" />
                      <path d="M4 12h16" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Storage & OS</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{product?.storageSize || '256GB'} {product?.storageType || 'SSD'} • {product?.operatingSystem || 'Windows Pro'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 group">
                  <div className="w-11 h-11 rounded-xl bg-[#0063B8] flex items-center justify-center text-white shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 3h18v14H3zM12 17v4M8 21h8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1A1A1A] mb-1">Warranty</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{product?.warranty || '1 Year Direct'} Warranty</p>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function TickerSection() {
  const items = [
    '50-Point Quality Inspection',
    'Certified Refurbished Laptops',
    'Battery Health Tested & Verified',
    'Genuine Licensed Windows',
    'Fast Dispatch Across India',
    'Direct Warranty Support',
    'Best Value Professional Devices',
    '50-Point Quality Inspection',
    'Certified Refurbished Laptops',
    'Battery Health Tested & Verified',
    'Genuine Licensed Windows',
    'Fast Dispatch Across India',
    'Direct Warranty Support',
    'Best Value Professional Devices',
  ];

  return (
    <div
      className="py-4 ticker-wrapper overflow-hidden mb-8"
      style={{
        background: '#060F1D',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      }}
    >
      <div className="flex gap-0 animate-ticker" style={{ width: 'max-content' }}>
        {items?.map((item, i) => (
          <span key={i} className="flex items-center gap-10 px-10">
            <span
              className="text-xs font-bold uppercase tracking-[0.25em] font-fraunces"
              style={{ color: 'rgba(255, 255, 255, 0.97)' }}
            >
              {item}
            </span>
            <span className="text-[#0063B8] text-lg opacity-90">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TechnicalSpecificationsSection({ product, sectionRef }) {
  const specs = [
    ['Brand', product?.brand || '-'],
    ['Model', product?.model || product?.name || '-'],
    ['Processor', product?.processor || 'Intel Core i5'],
    ['Generation', product?.generation || '10th Gen'],
    ['RAM', product?.ramSize || '16GB'],
    ['Storage', `${product?.storageSize || '256GB'} ${product?.storageType || 'SSD'}`.trim()],
    ['Display', product?.screenSize || '14"'],
    ['Operating System', product?.operatingSystem || 'Windows 10 Pro'],
    ['WiFi', product?.wifi || 'Yes'],
    ['Bluetooth', product?.bluetooth || 'Yes'],
    ['Webcam', product?.webcam || 'Yes'],
    ['Battery', product?.battery || 'Long-lasting'],
    ['Ports', product?.ports || 'USB, HDMI, Power'],
    ['Audio', product?.audio || 'Stereo speakers'],
    ['Condition', product?.condition || 'Refurbished'],
  ];

  return (
    <section ref={sectionRef} className="blazer-tech-specs-section">
      <div className="blazer-tech-specs-container">
        <div className="blazer-tech-specs-header">
          <div className="blazer-tech-kicker">Technical Specifications</div>
          <h2>Built For Daily Performance</h2>
          <p>Complete hardware and feature details for this refurbished device, fetched live from catalog data.</p>
        </div>

        <div className="blazer-tech-highlights">
          <div className="blazer-tech-highlight-card">
            <Cpu size={18} />
            <div>
              <span>Processor</span>
              <strong>{product?.processor || 'Intel Core i5'}</strong>
            </div>
          </div>
          <div className="blazer-tech-highlight-card">
            <HardDrive size={18} />
            <div>
              <span>Storage</span>
              <strong>{`${product?.storageSize || '256GB'} ${product?.storageType || 'SSD'}`}</strong>
            </div>
          </div>
          <div className="blazer-tech-highlight-card">
            <Monitor size={18} />
            <div>
              <span>Display</span>
              <strong>{product?.screenSize || '14"'}</strong>
            </div>
          </div>
          <div className="blazer-tech-highlight-card">
            <ShieldCheck size={18} />
            <div>
              <span>Condition</span>
              <strong>{product?.condition || 'Refurbished'}</strong>
            </div>
          </div>
        </div>

        <div className="blazer-tech-specs-grid" role="table" aria-label="Technical Specifications">
          {specs.map(([label, value]) => (
            <div key={label} className="blazer-tech-spec-row" role="row">
              <div className="blazer-tech-spec-label" role="cell">{label}</div>
              <div className="blazer-tech-spec-value" role="cell">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductSupportCTA({ sectionRef }) {
  const supportPhone = '8688090217';
  const supportPhoneDisplay = '+91 86880 90217';
  const supportMessage = 'Hi Nexawave, I need help choosing the right refurbished laptop.';

  return (
    <section ref={sectionRef} className="blazer-support-cta-section">
      <div className="blazer-support-cta-container">
        <div className="blazer-support-cta-content">
          <div className="blazer-support-cta-kicker">Need Help Deciding?</div>
          <h3>For Any Questions, Message Or Call Us</h3>
          <p>Talk to our experts for model selection, upgrades, warranty clarity, and business/bulk recommendations.</p>
          <div className="blazer-support-cta-meta">Response during business hours: usually within minutes.</div>
        </div>

        <div className="blazer-support-cta-actions">
          <a
            href={`https://wa.me/91${supportPhone}?text=${encodeURIComponent(supportMessage)}`}
            target="_blank"
            rel="noreferrer"
            className="blazer-support-btn blazer-support-btn-whatsapp"
            aria-label="Message on WhatsApp"
          >
            <MessageCircle size={16} />
            Message on WhatsApp
          </a>
          <a
            href={`tel:${supportPhone}`}
            className="blazer-support-btn blazer-support-btn-call"
            aria-label={`Call ${supportPhoneDisplay}`}
          >
            <PhoneCall size={16} />
            Call {supportPhoneDisplay}
          </a>
        </div>
      </div>
    </section>
  );
}


export default function AccessoryProductPage() {
  const { productId: urlProductId } = useParams();
  const productId = urlProductId || '2xx0efU2gADeID1nW1wf'; // Default to the requested product if no param
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showCartPopup, addedProduct, handleEnhancedAddToCart, closeCartPopup } = useEnhancedAddToCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isBarVisible, setIsBarVisible] = React.useState(false);
  const [selectedRAM, setSelectedRAM] = React.useState(null);
  const [selectedStorage, setSelectedStorage] = React.useState(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);

  const sectionRef = useRef(null);
  const mainCtaRef = useRef(null);
  const techSpecsRef = useRef(null);
  const supportCtaRef = useRef(null);


  const chapterRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      let foundProduct = null;
      const accessoriesCollectionRef = collection(db, 'products', 'accessories', 'items');

      // 1. Try finding by ID first
      const docRef = doc(accessoriesCollectionRef, productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        foundProduct = { id: docSnap.id, ...docSnap.data() };
      } 
      
      // 2. If not found by ID, try finding by slug
      if (!foundProduct) {
        const q = query(accessoriesCollectionRef, where('slug', '==', productId));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docRes = querySnapshot.docs[0];
          foundProduct = { id: docRes.id, ...docRes.data() };
        }
      }

      if (foundProduct) {
        setProduct(foundProduct);
        
        // 3. Self-healing: Update slug if it's missing (for future lookups)
        const currentSlug = generateProductSlug(foundProduct);
        if (foundProduct.slug !== currentSlug) {
          try {
            await updateDoc(doc(accessoriesCollectionRef, foundProduct.id), {
              slug: currentSlug
            });
          } catch (updateErr) {
            console.warn('Could not update slug in Firestore:', updateErr);
          }
        }
      } else {
        setError('Product not found');
      }
    } catch (err) {
      setError('Failed to load product');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Guard for non-browser environments and return early if loading
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (loading) return;

    const handleScroll = () => {
      // Show sticky CTA only after the primary CTA block has scrolled out of view.

      const ctaRect = mainCtaRef.current?.getBoundingClientRect();
      if (!ctaRect) {
        setIsBarVisible(false);
        return;
      }

      if (ctaRect.bottom <= 0) {
        const footer = document.querySelector('footer');
        if (footer && footer.getBoundingClientRect().top <= window.innerHeight) {
          setIsBarVisible(false);
        } else {
          setIsBarVisible(true);
        }
      } else {
        setIsBarVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    if (chapterRef.current) observer.observe(chapterRef.current);
    itemRefs.current.forEach((el) => el && observer.observe(el));

    // GSAP Animations
    const ctx = gsap.context(() => {
      const boxes = document.querySelectorAll('.feature-box');
      const centerImg = document.querySelector('.center-laptop-image');

      if (centerImg) {
        gsap.fromTo(centerImg,
          { scale: 0.8, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 1.5,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: centerImg,
              start: 'top 80%',
            }
          }
        );
      }

      boxes.forEach((box, i) => {
        gsap.fromTo(box,
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            delay: i * 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: box,
              start: 'top 85%',
            }
          }
        );
      });

      if (techSpecsRef.current) {
        gsap.fromTo(
          techSpecsRef.current,
          { y: 42, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: techSpecsRef.current,
              start: 'top 85%',
            }
          }
        );

        const specRows = techSpecsRef.current.querySelectorAll('.blazer-tech-spec-row');
        if (specRows.length > 0) {
          gsap.fromTo(
            specRows,
            { y: 20, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.55,
              stagger: 0.04,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: techSpecsRef.current,
                start: 'top 80%',
              }
            }
          );
        }
      }

      if (supportCtaRef.current) {
        gsap.fromTo(
          supportCtaRef.current,
          { y: 34, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.75,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: supportCtaRef.current,
              start: 'top 90%',
            }
          }
        );
      }
    }, sectionRef);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      observer.disconnect();
      ctx.revert();
    };
  }, [loading]);

  const displayImages = product?.imageUrls?.length > 0 ? product.imageUrls : sliderImages;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  // Touch/Swipe handlers for image navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextImage();
    if (isRightSwipe) prevImage();
  };

  const calculateFinalPrice = () => {
    let finalPrice = product?.price || 0;
    if (selectedRAM) finalPrice += selectedRAM.priceIncrease;
    if (selectedStorage) finalPrice += selectedStorage.priceIncrease;
    return finalPrice;
  };

  const calculateSavings = () => {
    if (!product?.originalPrice || !product?.price) return 0;
    return product.originalPrice - product.price;
  };

  const formatPrice = (price) => {
    return `₹${price?.toLocaleString() || '0'}`;
  };

  const handleAddToCartClick = (buttonElement) => {
    soundEffects.playAddToCartSound();
    handleEnhancedAddToCart(product, selectedRAM, selectedStorage, buttonElement);
  };

  if (loading) {
    return (
      <div className="product-page">
        <div className="loading-hero">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h2>Loading Premium Device...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-page">
        <div className="error-hero">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Device Not Found</h2>
            <button onClick={() => navigate('/laptops')} className="btn-primary">
              Browse All Laptops
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${product?.brand} ${product?.model || product?.name} | Best Price Accessory | Nexawave Technology`}</title>
        <meta name="description" content={`Buy ${product?.brand} ${product?.model || product?.name} at the best price. High-quality accessory for your computing needs. Guaranteed compatible and tested.`} />
        <meta name="keywords" content={`${product?.brand} ${product?.model}, ${product?.brand} accessory, best price ${product?.model}, Nexawave technology accessories`} />
        
        {/* Product Schema for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": `${product?.brand} ${product?.model || product?.name}`,
            "image": product?.imageUrls || [],
            "description": `High-quality ${product?.brand} ${product?.model} accessory. Reliable performance and durable build.`,
            "brand": {
              "@type": "Brand",
              "name": product?.brand
            },
            "offers": {
              "@type": "Offer",
              "url": (typeof window !== 'undefined') ? window.location.href : "",
              "priceCurrency": "INR",
              "price": calculateFinalPrice(),
              "availability": product?.availability === "Available" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "itemCondition": "https://schema.org/NewCondition"
            }
          })}
        </script>
      </Helmet>

    <section
      id="blazer-edit"
      ref={sectionRef}
      className="relative" style={{ backgroundColor: '#FFFFFF', paddingTop: '0.25rem', paddingBottom: '2.1rem', overflowX: 'hidden' }}>

      {/* Chapter number watermark */}
      <div
        className="chapter-number"
        style={{ top: '5%', right: '-2%', opacity: 0.1, color: '#0063B8' }}
        aria-hidden="true">

        X
      </div>

      <div className="max-w-7xl mx-auto px-6">

        {/* Hero piece + story */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
          <div
            ref={(el) => { itemRefs.current[0] = el; }}
            className="reveal-left relative">

            <div className="blazer-showcase">
              <div className="blazer-main-display">
                {/* Slider Controls */}
                {displayImages.length > 1 && (
                  <>
                    <button className="slider-arrow left" onClick={prevImage} aria-label="Previous image">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
                    </button>
                    <button className="slider-arrow right" onClick={nextImage} aria-label="Next image">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                    </button>
                  </>
                )}

                {/* Single Image Display */}
                <img
                  src={displayImages[currentImageIndex]}
                  alt={`${product?.name} view ${currentImageIndex + 1}`}
                  className="blazer-product-image"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                />

                {/* Image Counter */}
                {displayImages.length > 1 && (
                  <div className="blazer-image-counter">
                    {currentImageIndex + 1} / {displayImages.length}
                  </div>
                )}

                {/* Price tag overlay */}
                <div
                  className="blazer-price-tag"
                  style={{
                    background: '#0063B8',
                    padding: '8px 14px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px'
                  }}>
                  <div className="font-handwritten text-2xl" style={{ color: '#FFFFFF', fontFamily: 'Caveat, cursive' }}>{formatPrice(calculateFinalPrice())}</div>
                  <div className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.9)', fontSize: 9 }}>Inspected</div>
                </div>
              </div>

              {/* Thumbnail Gallery */}
              {displayImages.length > 1 && (
                <div className="blazer-image-gallery">
                  {displayImages.map((url, index) => (
                    <div
                      key={index}
                      className={`blazer-gallery-thumb ${currentImageIndex === index ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img src={url} alt={`View ${index + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Story card */}
          <div
            ref={(el) => { itemRefs.current[1] = el; }}
            className="reveal-right">

            <div className="story-card" style={{
              background: 'rgba(6, 15, 29, 0.95)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderLeft: '4px solid #0063B8',
              borderRadius: '12px'
            }}>
              <div className="flex items-center justify-between mb-6">
                <div className="tag-label" style={{ background: '#0063B8', color: '#FFFFFF' }}>{product?.brand || 'Brand'}</div>
                <div className="flex items-center gap-2">
                  <span className="scarce-dot" style={{ background: '#0063B8' }} />
                  <span className="font-body text-xs font-bold tracking-wide" style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 10 }}>In Stock</span>
                </div>
              </div>

              <h1 className="font-serif-display text-3xl mb-2" style={{ fontFamily: '"Montserrat", serif', color: '#FFFFFF' }}>
                {product?.name}<br />
                <em style={{ color: '#0063B8', fontSize: '0.82em' }}>{product?.processor} · {product?.ramSize} RAM</em>
              </h1>

              <div className="grid grid-cols-2 gap-4 my-6 py-6" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>

                {/* Processor */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1, flexShrink: 0 }}>
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <rect x="9" y="9" width="6" height="6" />
                    <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: '#0063B8', opacity: 0.8, fontSize: 9 }}>Processor</span>
                    <span className="font-body text-sm font-semibold" style={{ color: '#FFFFFF' }}>{product?.processor}</span>
                  </div>
                </div>

                {/* Memory */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1, flexShrink: 0 }}>
                    <rect x="2" y="7" width="20" height="10" rx="2" />
                    <path d="M7 7V5M12 7V5M17 7V5M7 17v2M12 17v2M17 17v2" />
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: '#0063B8', opacity: 0.8, fontSize: 9 }}>Memory</span>
                    <span className="font-body text-sm font-semibold" style={{ color: '#FFFFFF' }}>{product?.ramSize}</span>
                  </div>
                </div>

                {/* Storage */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1, flexShrink: 0 }}>
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12" />
                    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: '#0063B8', opacity: 0.8, fontSize: 9 }}>Storage</span>
                    <span className="font-body text-sm font-semibold" style={{ color: '#FFFFFF' }}>{product?.storageSize} {product?.storageType}</span>
                  </div>
                </div>

                {/* Warranty */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0063B8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1, flexShrink: 0 }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: '#0063B8', opacity: 0.8, fontSize: 9 }}>Warranty</span>
                    <span className="font-body text-sm font-semibold" style={{ color: '#FFFFFF' }}>{product?.warranty}</span>
                  </div>
                </div>

              </div>

              <p className="font-body text-sm leading-relaxed mb-6" style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
                {product?.description || "Precision-engineered carbon fiber chassis. The backlit keyboard feels like typing on silence. Each unit has been deeply inspected and re-certified for its second life in a boardroom or a coffee shop."}
              </p>

              <div className="auth-badge mb-6" style={{ borderColor: 'rgba(255, 255, 255, 0.15)', color: '#FFFFFF', background: 'rgba(255, 255, 255, 0.05)', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', fontSize: '10px', fontWeight: 'bold' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#0063B8">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                Authenticated & Grade A+ Certified
              </div>

              {/* DETAILED PRICING BLOCK */}
              <div className="price-display-block">
                <div className="current-price" style={{ color: '#FFFFFF' }}>{formatPrice(calculateFinalPrice())}</div>
                {product?.originalPrice > product?.price && (
                  <div className="price-meta">
                    <span className="original-price" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatPrice(product.originalPrice)}</span>
                    <span className="savings-label" style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF', borderColor: 'rgba(255, 255, 255, 0.2)' }}>Save {formatPrice(calculateSavings())}</span>
                  </div>
                )}
              </div>

              {/* UPGRADE OPTIONS */}

              <div className="mt-8">
                {product?.ramUpgradeOptions && product.ramUpgradeOptions.length > 0 && (
                  <div className="mb-6">
                    <div className="font-body text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#FFFFFF', opacity: 0.9, fontSize: 9 }}>RAM Upgrade</div>
                    <div className="options-group">
                      <div
                        className={`option-chip ${!selectedRAM ? 'selected' : ''}`}
                        onClick={() => setSelectedRAM(null)}
                      >
                        {product.ramSize} (Default)
                      </div>
                      {product.ramUpgradeOptions.map((opt, idx) => (
                        <div
                          key={idx}
                          className={`option-chip ${selectedRAM === opt ? 'selected' : ''}`}
                          onClick={() => setSelectedRAM(opt)}
                        >
                          {opt.name} (+{formatPrice(opt.priceIncrease)})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {product?.storageUpgradeOptions && product.storageUpgradeOptions.length > 0 && (
                  <div className="mb-6">
                    <div className="font-body text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#FFFFFF', opacity: 0.9, fontSize: 9 }}>Storage Upgrade</div>
                    <div className="options-group">
                      <div
                        className={`option-chip ${!selectedStorage ? 'selected' : ''}`}
                        onClick={() => setSelectedStorage(null)}
                      >
                        {product.storageSize} (Default)
                      </div>
                      {product.storageUpgradeOptions.map((opt, idx) => (
                        <div
                          key={idx}
                          className={`option-chip ${selectedStorage === opt ? 'selected' : ''}`}
                          onClick={() => setSelectedStorage(opt)}
                        >
                          {opt.name} (+{formatPrice(opt.priceIncrease)})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons for DESKTOP */}

              <div ref={mainCtaRef} className="flex gap-4 mt-8 flex-wrap md:flex-nowrap">
                <button
                  className="btn-brass cart-btn flex-1 justify-center"
                  style={{ letterSpacing: '0.1em' }}
                  onClick={(e) => handleAddToCartClick(e.target)}
                  disabled={product?.availability !== 'Available'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-2 add-to-cart-icon">
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                    <path d="M3 6h18M16 10a4 4 0 01-8 0" />
                  </svg>
                  <span>Add to Cart</span>
                </button>
                <button
                  className="btn-brass buy-btn flex-1 justify-center"
                  disabled={product?.availability !== 'Available'}
                  onClick={() => {
                    handleEnhancedAddToCart(product, selectedRAM, selectedStorage);
                    setTimeout(() => navigate('/cart'), 400); // Small delay for animation
                  }}
                >
                  <span>Buy Now</span>
                </button>
              </div>
            </div>
          </div>
        </div>


        <ProductImageCarousel />

        <TickerSection />

      </div>

      {/* MOBILE STICKY CTA BAR */}
      <div className={`mobile-cta-bar ${isBarVisible ? 'visible' : ''}`} style={{ alignItems: 'center' }}>
        <div className="w-[60%] md:w-auto md:flex-1 pr-2 md:pr-0">
          <div className="font-serif-display text-sm truncate md:whitespace-normal" style={{ color: '#FFFFFF' }}>{product?.name}</div>
          <div className="font-handwritten text-xl md:text-lg" style={{ color: '#0063B8' }}>{formatPrice(calculateFinalPrice())}</div>
        </div>
        <div className="w-[40%] md:w-auto flex flex-col md:flex-row gap-2">
          <button 
            className="btn-brass cart-btn w-full md:w-auto flex justify-center items-center px-1 py-2 text-[11px] md:px-4 md:py-2.5 md:text-[10px]" 
            onClick={(e) => handleAddToCartClick(e.target)}
            disabled={product?.availability !== 'Available'}
          >
            <span>Add to Cart</span>
          </button>
          <button 
            className="btn-brass buy-btn w-full md:w-auto flex justify-center items-center px-1 py-2 text-[11px] md:px-4 md:py-2.5 md:text-[10px]" 
            disabled={product?.availability !== 'Available'}
            onClick={() => {
              handleEnhancedAddToCart(product, selectedRAM, selectedStorage);
              setTimeout(() => navigate('/cart'), 400);
            }}
          >
            <span>Buy Now</span>
          </button>
        </div>
      </div>

      <CateringKitSection staggerRef={(el) => { itemRefs.current[3] = el; }} product={product} />
      <TechnicalSpecificationsSection product={product} sectionRef={techSpecsRef} />
      <ProductSupportCTA sectionRef={supportCtaRef} />
      
      {/* Cart Popup */}
      <CartPopup 
        isVisible={showCartPopup}
        onClose={closeCartPopup}
        addedProduct={addedProduct}
      />
    </section>
    </>
  );
}

